import TopImage from "../assets/logocqhse.png";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useState, useRef, useEffect } from "react";

import {
  LogOut,
  Menu,
  X,
  ShieldCheck,
} from "lucide-react";

import { showConfirm } from "../utils/confirm";
import { JWT, ROUTES } from "../config/constants";
import { clearAllSessionStorage } from "../utils/sessionStorage";
import { useCurrentUser } from "../hooks/useCurrentUser";

const Header = ({ toggleSidebar }) => {
  const { userName, projectName, isAdmin } =
    useCurrentUser();

  const location = useLocation();
  const navigate = useNavigate();

  const isDashboard =
    location.pathname.startsWith("/dashboard");

  const [menuOpen, setMenuOpen] = useState(false);

  const menuRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setMenuOpen(false);
      }
    };

    document.addEventListener(
      "mousedown",
      handleClickOutside,
    );

    return () =>
      document.removeEventListener(
        "mousedown",
        handleClickOutside,
      );
  }, []);

  const toggleMenu = () =>
    setMenuOpen((prev) => !prev);

  const handleLogout = () => {
    showConfirm({
      title: "Konfirmasi Logout",
      message:
        "Apakah Anda yakin ingin keluar dari aplikasi?",
      confirmLabel: "Ya, Logout",
      cancelLabel: "Batal",
      onConfirm: () => {
        localStorage.removeItem(JWT.TOKEN_KEY);
        localStorage.removeItem(
          JWT.PROJECT_ID_KEY,
        );
        localStorage.removeItem(
          JWT.PROJECT_NAME_KEY,
        );
        localStorage.removeItem(JWT.NAMA_KEY);

        clearAllSessionStorage();

        navigate(ROUTES.LOGIN);
      },
    });
  };

  const linkClasses = (path) =>
    `relative px-4 py-2 rounded-2xl text-sm font-medium transition-all duration-300 ${
      location.pathname === path
        ? "bg-cyan-50 text-cyan-700 shadow-sm"
        : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
    }`;

  // =========================
  // PUBLIC DASHBOARD HEADER
  // =========================
  if (isDashboard) {
    return (
      <header className="fixed top-0 left-0 z-50 w-full">
        {/* Glow */}
        <div className="absolute inset-0 bg-white/70 backdrop-blur-2xl border-b border-gray-200 shadow-sm" />

        <div className="relative h-16 px-5 flex items-center justify-between">
          {/* Left */}
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-2xl bg-cyan-50 border border-cyan-100 flex items-center justify-center">
              <ShieldCheck
                size={18}
                className="text-cyan-600"
              />
            </div>

            <div>
              <p className="text-sm font-semibold text-gray-900">
                Dashboard Portal
              </p>

              <p className="text-[11px] text-gray-500">
                Engineering Strategic Digital Platform
              </p>
            </div>
          </div>

          {/* Center */}
          <nav className="hidden md:flex items-center gap-2">
            <Link
              to={ROUTES.LESSONS_DASHBOARD}
              className={linkClasses(
                ROUTES.LESSONS_DASHBOARD,
              )}
            >
              Lessons
            </Link>

            <Link
              to={ROUTES.VAVE_DASHBOARD}
              className={linkClasses(
                ROUTES.VAVE_DASHBOARD,
              )}
            >
              VA / VE
            </Link>
          </nav>

          {/* Right */}
          <div className="flex items-center gap-3">
            <img
              src={TopImage}
              alt="Logo"
              className="h-10 object-contain"
            />
          </div>
        </div>
      </header>
    );
  }

  // =========================
  // AUTHENTICATED HEADER
  // =========================
  return (
    <header className="fixed top-0 left-0 z-50 w-full">
      {/* Background */}
      <div className="absolute inset-0 bg-white/70 backdrop-blur-2xl border-b border-gray-200 shadow-sm" />

      {/* Glow */}
      <div className="absolute top-[-40px] left-[20%] w-[240px] h-[240px] bg-cyan-200/20 rounded-full blur-3xl" />

      <div className="relative h-16 px-5 flex items-center justify-between">
        {/* LEFT */}
        <div className="flex items-center gap-3">
          {/* Mobile Menu */}
          <button
            className="md:hidden w-10 h-10 rounded-2xl border border-gray-200 bg-white flex items-center justify-center shadow-sm text-gray-700"
            onClick={toggleMenu}
          >
            {menuOpen ? (
              <X size={18} />
            ) : (
              <Menu size={18} />
            )}
          </button>

          {/* User */}
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-cyan-50 border border-cyan-100 flex items-center justify-center shadow-sm">
              <span className="text-sm font-semibold text-cyan-700">
                {userName?.charAt(0)?.toUpperCase()}
              </span>
            </div>

            <div className="hidden sm:block">
              <p className="text-sm font-semibold text-gray-900 leading-none">
                {userName}
              </p>

              <p className="text-xs text-gray-500 mt-1">
                {projectName}
              </p>
            </div>
          </div>
        </div>

        {/* DESKTOP NAV */}
        <nav className="hidden md:flex items-center gap-2">
          <Link
            to={ROUTES.HOME}
            className={linkClasses(ROUTES.HOME)}
          >
            Home
          </Link>

          <Link
            to={ROUTES.LOGO}
            className={linkClasses(ROUTES.LOGO)}
          >
            Logo
          </Link>

          {isAdmin && (
            <>
              <Link
                to={ROUTES.CATEGORIES}
                className={linkClasses(
                  ROUTES.CATEGORIES,
                )}
              >
                Categories
              </Link>

              <Link
                to={ROUTES.USER_MANAGEMENT}
                className={linkClasses(
                  ROUTES.USER_MANAGEMENT,
                )}
              >
                Users
              </Link>

              <Link
                to={ROUTES.PROJECT}
                className={linkClasses(
                  ROUTES.PROJECT,
                )}
              >
                Projects
              </Link>
            </>
          )}
        </nav>

        {/* RIGHT */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleLogout}
            className="group w-10 h-10 rounded-2xl border border-red-100 bg-white flex items-center justify-center shadow-sm hover:bg-red-50 transition-all duration-300"
          >
            <LogOut
              size={17}
              className="text-red-500 group-hover:rotate-6 transition-transform"
            />
          </button>

          <div className="hidden sm:flex items-center justify-center w-11 h-11 rounded-2xl bg-white border border-gray-200 shadow-sm">
            <img
              src={TopImage}
              alt="Logo"
              className="h-7 object-contain"
            />
          </div>
        </div>
      </div>

      {/* MOBILE MENU */}
      {menuOpen && (
        <div
          ref={menuRef}
          className="md:hidden relative mx-4 mt-3 rounded-3xl border border-gray-200 bg-white/90 backdrop-blur-2xl shadow-2xl overflow-hidden"
        >
          <div className="p-4 flex flex-col gap-2">
            <Link
              to={ROUTES.HOME}
              onClick={() => setMenuOpen(false)}
              className={linkClasses(ROUTES.HOME)}
            >
              Home
            </Link>

            <Link
              to={ROUTES.LOGO}
              onClick={() => setMenuOpen(false)}
              className={linkClasses(ROUTES.LOGO)}
            >
              Logo
            </Link>

            {isAdmin && (
              <>
                <Link
                  to={ROUTES.CATEGORIES}
                  onClick={() =>
                    setMenuOpen(false)
                  }
                  className={linkClasses(
                    ROUTES.CATEGORIES,
                  )}
                >
                  Categories
                </Link>

                <Link
                  to={ROUTES.USER_MANAGEMENT}
                  onClick={() =>
                    setMenuOpen(false)
                  }
                  className={linkClasses(
                    ROUTES.USER_MANAGEMENT,
                  )}
                >
                  Users
                </Link>

                <Link
                  to={ROUTES.PROJECT}
                  onClick={() =>
                    setMenuOpen(false)
                  }
                  className={linkClasses(
                    ROUTES.PROJECT,
                  )}
                >
                  Projects
                </Link>
              </>
            )}

            {/* Logout */}
            <button
              onClick={() => {
                setMenuOpen(false);
                handleLogout();
              }}
              className="mt-2 flex items-center gap-2 px-4 py-3 rounded-2xl text-red-600 hover:bg-red-50 transition-all duration-300"
            >
              <LogOut size={17} />
              <span className="text-sm font-medium">
                Logout
              </span>
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
